import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useDebounce } from 'react-use';
import { getCrossLicensedLocale } from '@/common/utils/license';
import { useCore } from '$api-clients/hooks';
import type { ClientGroup, CreateOfferFormData } from '../types';

export const useGetClientGroupsForState = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [results, setResults] = useState<ClientGroup[]>([]);
  const state = useWatch<CreateOfferFormData, 'state'>({
    name: 'state',
  });

  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    300,
    [searchTerm],
  );

  const marketFacets = useMemo(() => {
    if (!state) return null;
    const crossLicensedStates = getCrossLicensedLocale(state);
    return crossLicensedStates
      ? crossLicensedStates.map((s) => `market:${s}`).join(',')
      : `market:${state}`;
  }, [state]);

  const { isLoading, data, error } = useCore(
    '/client-groups',
    marketFacets
      ? {
          params: {
            query: {
              includeContacts: true,
              limit: debouncedSearchTerm ? undefined : 10,
              search: debouncedSearchTerm,
              facets: `${marketFacets},isArchived:false`,
            },
          },
        }
      : null,
  );

  if (error) {
    toast('error', 'There was a problem searching for buyers');
    datadogRum.addError(error);
  }

  useEffect(() => {
    // save previous results so we can show them while a subsequent request is loading
    if (data?.results) {
      setResults(data.results);
    }
  }, [data]);

  return {
    loading: isLoading,
    clientGroups: results,
    setSearchTerm,
    debouncedSearchTerm,
  };
};
